import { useState } from 'react'
import { Box, CircularProgress } from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import { FONT_SANS, FONT_MONO, TOKENS } from './OptimiserDesign'
import { OptimiserScenario, useCreateOptimiserScenario, useOptimiserScenarios } from './Optimiser.data'

interface ScenarioSelectorProps {
  scenarioId: string | null
  onSelect: (scenarioId: string | null) => void
}

const Pill = ({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick: () => void
  children: React.ReactNode
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'inline-flex',
      alignItems: 'center',
      px: '10px',
      py: '4px',
      borderRadius: '4px',
      fontSize: 12,
      fontWeight: active ? 700 : 400,
      fontFamily: FONT_SANS,
      cursor: 'pointer',
      border: '1px solid',
      borderColor: active ? TOKENS.brandTeal : TOKENS.line300,
      background: active ? TOKENS.brandTeal : TOKENS.surface0,
      color: active ? '#fff' : TOKENS.ink700,
      userSelect: 'none',
      whiteSpace: 'nowrap',
      flexShrink: 0,
      transition: 'all 0.12s',
      '&:hover': active
        ? {}
        : { borderColor: TOKENS.brandTeal, color: TOKENS.brandTeal },
    }}
  >
    {children}
  </Box>
)

const ScenarioDate = ({ iso }: { iso: string }) => {
  const d = new Date(iso)
  const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec']
  return (
    <Box
      component="span"
      sx={{ fontFamily: FONT_MONO, fontSize: 10, color: 'inherit', opacity: 0.7, ml: '5px' }}
    >
      {d.getDate()} {MONTHS[d.getMonth()]}
    </Box>
  )
}

export const ScenarioSelector = ({ scenarioId, onSelect }: ScenarioSelectorProps) => {
  const { data, isLoading } = useOptimiserScenarios()
  const createMutation = useCreateOptimiserScenario()

  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')

  const handleCreate = () => {
    const name = newName.trim()
    if (!name) return
    createMutation.mutate(
      { name },
      {
        onSuccess: (scenario: OptimiserScenario) => {
          setCreating(false)
          setNewName('')
          onSelect(scenario.scenario_id)
        },
      },
    )
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleCreate()
    if (e.key === 'Escape') {
      setCreating(false)
      setNewName('')
    }
  }

  return (
    <Box
      sx={{
        px: '36px',
        py: '8px',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
        borderBottom: `1px solid ${TOKENS.line200}`,
        background: TOKENS.panelHead,
        flexShrink: 0,
        overflowX: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
      }}
    >
      {/* Label */}
      <Box
        sx={{
          fontSize: 10,
          fontWeight: 700,
          textTransform: 'uppercase',
          letterSpacing: '0.07em',
          color: TOKENS.ink300,
          fontFamily: FONT_SANS,
          flexShrink: 0,
          mr: '4px',
        }}
      >
        Scenario
      </Box>

      {/* Base data */}
      <Pill active={scenarioId === null} onClick={() => onSelect(null)}>
        Base Data
      </Pill>

      {/* Scenario pills */}
      {isLoading && (
        <CircularProgress size={14} thickness={3} sx={{ color: TOKENS.ink300, ml: 0.5 }} />
      )}
      {data?.scenarios.map((s) => (
        <Pill
          key={s.scenario_id}
          active={scenarioId === s.scenario_id}
          onClick={() => onSelect(s.scenario_id)}
        >
          {s.name}
          <ScenarioDate iso={s.created_at} />
        </Pill>
      ))}

      {/* Create inline */}
      {creating ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <Box
            component="input"
            autoFocus
            placeholder="Scenario name…"
            value={newName}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNewName(e.target.value)}
            onKeyDown={handleKeyDown}
            sx={{
              px: '10px',
              py: '4px',
              border: `1px solid ${TOKENS.brandTeal}`,
              borderRadius: '4px',
              fontSize: 12,
              fontFamily: FONT_SANS,
              color: TOKENS.ink900,
              background: TOKENS.surface0,
              outline: 'none',
              width: 160,
            }}
          />
          <Box
            onClick={handleCreate}
            sx={{
              px: '10px',
              py: '4px',
              borderRadius: '4px',
              fontSize: 12,
              fontWeight: 700,
              fontFamily: FONT_SANS,
              cursor: createMutation.isPending ? 'default' : 'pointer',
              background: TOKENS.brandTeal,
              color: '#fff',
              opacity: createMutation.isPending ? 0.6 : 1,
              userSelect: 'none',
              flexShrink: 0,
            }}
          >
            {createMutation.isPending ? 'Creating…' : 'Create'}
          </Box>
          <Box
            onClick={() => { setCreating(false); setNewName('') }}
            sx={{
              px: '8px',
              py: '4px',
              borderRadius: '4px',
              fontSize: 12,
              fontFamily: FONT_SANS,
              cursor: 'pointer',
              color: TOKENS.ink400,
              userSelect: 'none',
              '&:hover': { color: TOKENS.ink700 },
            }}
          >
            Cancel
          </Box>
        </Box>
      ) : (
        <Box
          onClick={() => setCreating(true)}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '3px',
            px: '8px',
            py: '4px',
            borderRadius: '4px',
            fontSize: 12,
            fontFamily: FONT_SANS,
            cursor: 'pointer',
            color: TOKENS.ink400,
            border: `1px dashed ${TOKENS.line300}`,
            userSelect: 'none',
            flexShrink: 0,
            '&:hover': { borderColor: TOKENS.brandTeal, color: TOKENS.brandTeal },
          }}
        >
          <AddIcon sx={{ fontSize: 13 }} />
          New Scenario
        </Box>
      )}
    </Box>
  )
}
